<template>
  <div class="col-md-12">
    <div class="card">
      <div class="card-header">
        <h4 class="card-title">{{ title }}</h4>
      </div>
      <div class="card-body form">
        <div class="row">
          <div class="col-12 col-sm-8">
            <div class="form-group">
              <label for="inventory_description">Description</label>
              <input
                autocapitalize="off"
                autocomplete="off"
                autocorrect="off"
                autofocus=""
                role="combobox"
                spellcheck="false"
                type="text"
                name="description"
                class="form-control"
                id="inventory_description"
                placeholder="Search descriptions..."
                v-model="inventory.description"
                :disabled="title === 'Edit Inventory'"
                @keyup="itemLiveSearch(inventory.description)"
              />

              <div
                id="itemCustomLiveSearch"
                class="customLiveSearch"
                style="max-height: 250px"
                v-show="itemRows && itemRows.length">
                <template v-for="(item, index) in itemRows">
                  <div
                    :key="index"
                    class="customLiveSearchElement"
                    v-on:click="itemSelected(item)"
                  >
                    {{ item.description }}
                  </div>
                </template>
              </div>
              <span v-if="loading" class="text-muted"
                ><ThreeDots size="18px"></ThreeDots> Searching...</span
              >
            </div>
          </div>

          <div class="col-12 col-sm-4">
            <div class="form-group">
              <label for="inventory_internalId">Internal ID</label>
              <input
                type="text"
                name="internalId"
                class="form-control"
                id="inventory_internalId"
                placeholder=""
                v-model="inventory.internalId"
                :disabled="true"
              />
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-12 col-sm-6">
            <div class="form-group">
              <label for="inventory_htsCode">HTS Code</label>
              <input
                autocapitalize="off"
                autocomplete="off"
                autocorrect="off"
                autofocus=""
                role="combobox"
                spellcheck="false"
                type="text"
                name="htsCode"
                class="form-control"
                id="inventory_htsCode"
                placeholder="Search HTS codes..."
                v-model="inventory.htsCode"
                :disabled="title === 'Edit Inventory'"
                @keyup="htsLiveSearch(inventory.htsCode)"
              />

              <div
                id="htsCustomLiveSearch"
                class="customLiveSearch"
                style="max-height: 250px"
                v-show="htsRows && htsRows.length"
              >
                <template v-for="(row, index) in htsRows">
                  <div
                    :key="index"
                    class="customLiveSearchElement"
                    v-on:click="htsSelected(row)"
                  >
                    {{ row.htsCode + " - " + row.rollupDescription }}
                  </div>
                </template>
              </div>
              <span v-if="htsLoading" class="text-muted"
                ><ThreeDots size="18px"></ThreeDots> Searching...</span
              >
            </div>
          </div>
          <div class="col-12 col-sm-6">
            <div class="form-group">
              <label for="inventory_rollupDescription"
                >Rollup Description</label
              >
              <input
                type="text"
                name="rollupDescription"
                class="form-control"
                id="inventory_rollupDescription"
                placeholder=""
                v-model="inventory.rollupDescription"
                :disabled="readonly"
              />
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-12 col-sm-6">
            <div class="form-group">
              <label for="inventory_productCode">Product Code</label>
              <input
                type="text"
                name="productCode"
                class="form-control"
                id="inventory_productCode"
                placeholder=""
                v-model="inventory.productCode"
                :disabled="readonly"
              />
            </div>
          </div>
          <div class="col-12 col-sm-6">
            <div class="form-group">
              <label for="inventory_fda">FDA</label>
              <input
                type="text"
                name="fda"
                class="form-control"
                id="inventory_fda"
                placeholder=""
                v-model="inventory.fda"
                :disabled="readonly"
              />
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-12 col-sm-6">
            <div class="form-group">
              <label for="inventory_unitsPerCase">Units Per Case</label>
              <input
                type="text"
                name="unitsPerCase"
                class="form-control"
                id="inventory_unitsPerCase"
                placeholder=""
                v-model="inventory.unitsPerCase"
                v-validate="'required'"
                :disabled="readonly"
              />
            </div>
          </div>
          <div class="col-12 col-sm-6">
            <div class="form-group">
              <label for="inventory_pricePerUnit">Price Per Unit <font color='red'>{{netSuitePriceWarn}}</font></label>
              <input
                type="text"
                name="pricePerUnit"
                class="form-control"
                id="inventory_pricePerUnit"
                placeholder=""
                v-model="inventory.pricePerUnit"
              />
            </div>
          </div>
          <div class="col-12 col-sm-6">
            <div class="form-group">
              <label for="inventory_origin">Origin</label>
              <input
                type="text"
                name="origin"
                class="form-control"
                id="inventory_origin"
                placeholder="Country of origin"
                v-model="inventory.origin"
                :disabled="readonly"
              />
            </div>
          </div>
          <div class="col-12 col-sm-6">
            <div class="form-group">
              <label for="api_integration">API Integration</label>
              <input
                type="text"
                name="apiIntegration"
                class="form-control"
                id="api_integration"
                placeholder="API integration key"
                v-model="inventory.apiIntegration"
                :disabled="readonly"
              />
            </div>
          </div>
          <div class="col-12 col-sm-6">
            <div class="form-group">
              <label for="inventory_unpacked">Unpacked</label>
              <select
                class="form-control"
                name="unpacked"
                v-model="inventory.unpacked"
                id="inventory_unpacked"
              >
                <option :value="true">Yes</option>
                <option :value="false">No</option>
              </select>
            </div>
          </div>
          <div class="col-12 col-sm-6">
            <div class="form-group">
              <label for="inventory_isContainer">Is a container</label>
              <select
                class="form-control"
                name="isContainer"
                v-model="inventory.isContainer"
                id="inventory_isContainer"
              >
                <option :value="true">Yes</option>
                <option :value="false">No</option>
              </select>
            </div>
          </div>
          <div class="col-12 col-sm-6">
            <div class="form-group">
              <label for="inventory_shippingType">Type</label>
              <select class="form-control" name="type" v-model="inventory.type" id="inventory_type">
                          <option>None</option>
                          <option>Pallet</option>
                          <option>Skid</option>
                          <option>Slipsheet</option>
                          <option>Tote</option>
                          <option>Pallet Box</option>
                          <option>Skid Box</option>
                          <option>Bin</option>
                          <option>Basket</option>
                          <option>Rack</option>
                          <option>Carton</option>
                          <option>Bag</option>
                          <option>Drum</option>
                          <option>Crate</option>
              </select>
            </div>
          </div>
          <hr />

          <div class="col-12">
            <div class="card">
              <div class="card-header">
                <h5 class="card-title">Dimensions</h5>
              </div>
              <div class="card-body">
                <div class="row">
                  <div class="col-3">
                    <div class="form-group">
                      <label for="length">Length (inches)</label>
                      <input
                        type="text"
                        name="length"
                        class="form-control"
                        id="length"
                        placeholder="Length in inches"
                        v-model="inventory.dimensions.length"
                      />
                    </div>
                  </div>
                  <div class="col-3">
                    <div class="form-group">
                      <label for="width">Width (inches)</label>
                      <input
                        type="text"
                        name="width"
                        class="form-control"
                        id="width"
                        placeholder="Width in inches"
                        v-model="inventory.dimensions.width"
                      />
                    </div>
                  </div>
                  <div class="col-3">
                    <div class="form-group">
                      <label for="height">Height (inches)</label>
                      <input
                        type="text"
                        name="height"
                        class="form-control"
                        id="height"
                        placeholder="Height in inches"
                        v-model="inventory.dimensions.height"
                      />
                    </div>
                  </div>
                  <div class="col-3">
                    <div class="form-group">
                      <label for="weight">Weight (lbs)</label>
                      <input
                        type="text"
                        name="weight"
                        class="form-control"
                        id="weight"
                        placeholder="Weight in lbs"
                        v-model="inventory.dimensions.weight"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import inventoryService from "../../services/inventory.service";
import { ThreeDots } from "vue-loading-spinner";

var lastSearched;

export default {
  name: "InventoryForm",
  components: {
    ThreeDots,
  },
  props: ["title", "readonly"],
  data: () => ({
    inventory: {
      internalId: "",
      apiIntegration: "",
      description: "",
      rollupDescription: "",
      unitsPerCase: "",
      hts: {},
      htsCode: "",
      productCode: "",
      type: "",
      fda: "",
      pricePerUnit: "",
      origin: "XO",
      unpacked: false,
      isContainer: false,
      dimensions: {},
    },
    lastSearched: lastSearched,
    itemRows: [],
    htsRows: [],
    loading: false,
    htsLoading: false,
    latestPrice: null,
    searchTimeout: {}
  }),
  computed: {

    netSuitePriceWarn() {
      if (this.latestPrice && this.latestPrice != this.inventory.pricePerUnit) {
        return "(API price: " + this.latestPrice + ")";
      }
      return "";
    }

  },
  methods: {
    itemLiveSearch: async function (text) {
      if (this.searchTimeout.itemLiveSearch) {
        clearTimeout(this.searchTimeout.itemLiveSearch);
      }
      this.searchTimeout.itemLiveSearch = setTimeout (async () => {
        console.log("item search " + text);
        this.dissmissAutoCompletes();
        this.loading = false;
        if (text.length < 4) {
          this.inventory.internalId = ""; // reset
        } else {
          if (
            [
              "Raefton Technology Inc.",
              "Diva International",
              "Unique Global Products and Distribution Inc.",
            ].includes(localStorage.getItem("local_company"))
          ) {
            this.loading = true;
            this.liveSearching = true;
            const res = await inventoryService.search(text);
            if (this.liveSearching) {
              console.log(res);
              if (Array.isArray(res) && res.length > 0) {
                this.itemRows = [{ description: "Dismiss", _id: "-1" }, ...res];
                this.showAutoComplete("itemCustomLiveSearch");
              } else {
                this.inventory.internalId = "";
                this.dissmissAutoCompletes();
              }
            }
            this.loading = false;
          }
        }
      }, 1000);
    },
    itemSelected: async function (item) {
      console.log("itemSelected " + item.description + " " + item.pricePerUnit);
      this.liveSearching = false;
      this.dissmissAutoCompletes();

      if (item && item._id !== "-1") {
        this.inventory = {
          ...this.inventory,
          ...item,
          rollupDescription: item.rollupDescription
            ? item.rollupDescription
            : item.description,
          type: 'None',
          apiIntegration: 'NetSuite-4637327', // get from Company config when this is implemented
        };

        if (this.inventory.htsCode) {
          this.dissmissAutoCompletes();
          this.htsLiveSearch(this.inventory.htsCode, true);
        }

        this.inventory.isContainer = this.inventory.unitsPerCase > 1;
        this.inventory.pricePerUnit = Math.round(this.inventory.pricePerUnit*100) / 100;
      }
    },
    htsLiveSearch: async function (text, fromDescription = false) {
      console.log("HTS search " + this.parent);
      this.loading = false;
      if (text.length < 4) {
        this.dissmissAutoCompletes();
      } else {
        this.htsLoading = true;
        const res = await inventoryService.searchHTS(text);
        console.log(res);
        if (Array.isArray(res) && res.length > 0) {
          this.htsRows = [
            { htsCode: "Dismiss", rollupDescription: "", _id: "-1" },
            ...res,
          ];
          this.showAutoComplete("htsCustomLiveSearch");

          if (res.length === 1 && fromDescription) {
            this.inventory = {
              ...this.inventory,
              htsCode: res[0].htsCode,
              // rollupDescription: res[0].rollupDescription // remove to support multiple rollup descriptions for one hts
              productCode: res[0].productCode,
              fda: res[0].fda,
              hts: {
                ...res[0],
                rollupDescription: this.inventory.rollupDescription,
              },
              type: 'None',
              apiIntegration: 'NetSuite-4637327',

            };

            this.dissmissAutoCompletes();
          }
        } else {
          this.dissmissAutoCompletes();
        }
        this.htsLoading = false;
      }
    },
    htsSelected: async function (hts) {
      console.log("htsSelected " + hts.htsCode);
      this.liveSearching = false;
      this.dissmissAutoCompletes();

      if (hts && hts._id !== "-1") {
        const { rollupDescription, productCode, fda, htsCode } = hts;
        this.inventory = {
          ...this.inventory,
          htsCode,
          rollupDescription,
          productCode,
          fda,
          hts: hts,
        };
        console.log(this.inventory);
      }
    },
    dissmissAutoCompletes: function () {
      console.log("dissmissAutoCompletes");
      $(".customLiveSearch").hide();
    },

    showAutoComplete: function (id) {
      setTimeout(() => document.getElementById(id).style.display = "block");
    },
    getClearData: function () {
      let data = Object.assign(
        {
          type: "Inventory",
        },
        this.inventory
      );

      return data;
    },
    loadData: async function (data) {
      if (!data) return;

      this.inventory.internalId = data.internalId || "";
      this.inventory.apiIntegration = data.apiIntegration || "";
      this.inventory.description = data.description || "";
      this.inventory.rollupDescription = data.rollupDescription || "";
      this.inventory.unitsPerCase = data.unitsPerCase || "";
      this.inventory.htsCode = data.htsCode || "";
      this.inventory.productCode = data.productCode || "";
      this.inventory.type = data.type || "";
      this.inventory.fda = data.fda || "";
      this.inventory.pricePerUnit = data.pricePerUnit || "";
      this.inventory.origin = data.origin || "XO";
      this.inventory.unpacked = data.unpacked || false;
      this.inventory.isContainer = data.isContainer || false;
      this.inventory.dimensions = data.dimensions || {};

      const hts = await inventoryService.getHTS(
        data.htsCode,
        data.rollupDescription,
        data.productCode,
        data.fda
      );
      if (hts) this.inventory.hts = hts[0];

      const res = await inventoryService.search(this.inventory.description);
      if (Array.isArray(res) && res.length > 0) {
        this.latestPrice = Math.round(res[0].pricePerUnit*100) / 100;
      } 


    },
  },
};
</script>

<style scoped>
.su-group-addon {
  background-color: #eee !important;
  border: 1px solid #ccc !important;
}
</style>


// WEBPACK FOOTER //
// InventoryForm.vue?46c2c270